import { useEffect, useMemo, useState, useCallback } from "react";
import Swal from "../../../../utils/apparifySwal";
import { AlertTriangle, ArrowDown, ArrowUp, Check, Plus, Trash2, X } from "lucide-react";
import { businessSwalBase } from "../../../utils/businessSwal";
import { Button, DisclosureLink, IconButton } from "../../../../components/ui";

const EMPTY_ARR = [];

function clean(s) {
  return String(s || "").trim();
}

function unique(list) {
  return Array.from(new Set((list || EMPTY_ARR).map(clean).filter(Boolean)));
}

function makeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `opt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

/* ── Predefined option templates ── */
const OPTION_TEMPLATES = [
  { name: "Size", defaults: ["S", "M", "L", "XL"] },
  { name: "Color", defaults: ["Black", "White"] },
  { name: "Material", defaults: ["Cotton", "Polyester", "Linen"] },
  { name: "Fit", defaults: ["Slim", "Regular", "Oversized"] },
  { name: "Sleeve Length", defaults: ["Short Sleeve", "Long Sleeve", "Sleeveless"] },
];

/* ── Autocomplete suggestions by option type ── */
const SUGGESTIONS = {
  size: ["XXS", "XS", "S", "M", "L", "XL", "XXL", "2XL", "3XL"],
  color: ["Black", "White", "Red", "Blue", "Navy", "Green", "Gray", "Beige", "Brown", "Pink", "Yellow", "Orange", "Purple", "Cream", "Olive", "Maroon", "Teal"],
  material: ["Cotton", "Polyester", "Linen", "Silk", "Wool", "Nylon", "Denim", "Rayon", "Spandex"],
  fit: ["Slim", "Regular", "Relaxed", "Oversized", "Loose", "Tailored", "Skinny", "Boxy"],
  "sleeve length": ["Short Sleeve", "Long Sleeve", "Sleeveless", "3/4 Sleeve", "Cap Sleeve"],
};

/* ── Color preview map ── */
const COLOR_MAP = {
  black: "#000000", white: "#ffffff", red: "#ef4444", blue: "#3b82f6",
  navy: "#1e3a5f", green: "#22c55e", gray: "#6b7280", grey: "#6b7280",
  beige: "#f5f5dc", brown: "#92400e", pink: "#ec4899", yellow: "#eab308",
  orange: "#f97316", purple: "#a855f7", cream: "#fffdd0", olive: "#808000",
  maroon: "#800000", teal: "#14b8a6",
};

function getColorHex(value) {
  return COLOR_MAP[String(value || "").toLowerCase().trim()] || null;
}

function isColorOption(name) {
  return /colou?r/i.test(String(name || ""));
}

const VARIANT_WARNING_THRESHOLD = 50;
const VARIANT_DANGER_THRESHOLD = 100;

export default function StepOptions({ state, setState }) {
  // ✅ stable reference (no "|| []" inline)
  const options = useMemo(() => state?.options ?? EMPTY_ARR, [state?.options]);
  const variants = state?.variants || EMPTY_ARR;
  const hasExistingVariants = variants.length > 0;

  // draftValue must be keyed by stable option id (NOT idx)
  const [draftValue, setDraftValue] = useState({});

  const setOptions = useCallback(
    (next) => setState((p) => ({ ...p, options: next })),
    [setState]
  );

  /**
   * Ensure each option has a stable `id`.
   * This prevents input remount/focus loss while typing.
   */
  useEffect(() => {
    if (!options.length) return;

    const hasMissingId = options.some((o) => !o?.id);
    if (!hasMissingId) return;

    setOptions(
      options.map((o) => ({
        id: o?.id || makeId(),
        name: o?.name || "",
        values: Array.isArray(o?.values) ? o.values : [],
      }))
    );
  }, [options, setOptions]);

  const validOptions = useMemo(() => {
    return (options || EMPTY_ARR)
      .map((o) => ({
        id: o?.id,
        name: clean(o?.name),
        values: unique(o?.values),
      }))
      .filter((o) => o.name && o.values.length);
  }, [options]);

  const variantCount = useMemo(() => {
    if (!validOptions.length) return 0;
    return validOptions.reduce((acc, o) => acc * o.values.length, 1);
  }, [validOptions]);

  // Formula breakdown: "Size (3) × Color (4)"
  const formulaBreakdown = useMemo(() => {
    if (validOptions.length < 2) return null;
    return validOptions.map((o) => `${o.name} (${o.values.length})`).join(" × ");
  }, [validOptions]);

  // Duplicate option name detection
  const duplicateNames = useMemo(() => {
    const nameCount = {};
    options.forEach((o) => {
      const n = clean(o?.name).toLowerCase();
      if (n) nameCount[n] = (nameCount[n] || 0) + 1;
    });
    return new Set(Object.keys(nameCount).filter((n) => nameCount[n] > 1));
  }, [options]);

  // Used option names (for template button disabling)
  const usedNames = useMemo(() => {
    return new Set(options.map((o) => clean(o?.name).toLowerCase()).filter(Boolean));
  }, [options]);

  const issues = useMemo(() => {
    const out = [];

    if (!validOptions.length) out.push("Add at least one option with at least 1 value.");

    const hasEmptyName = options.some((o) => !clean(o?.name));
    if (hasEmptyName) out.push("Some option names are empty.");

    const hasEmptyVals = options.some((o) => clean(o?.name) && unique(o?.values).length === 0);
    if (hasEmptyVals) out.push("Some options have no values.");

    if (duplicateNames.size > 0) out.push("Duplicate option names detected.");

    return out;
  }, [options, validOptions, duplicateNames]);

  const ok = issues.length === 0;

  const addValues = useCallback(
    (optId) => {
      const raw = draftValue[optId] || "";
      const vals = raw.split(",").map(clean).filter(Boolean);
      if (!vals.length) return;

      setOptions(
        options.map((o) => {
          if (o.id !== optId) return o;
          return { ...o, values: unique([...(o.values || EMPTY_ARR), ...vals]) };
        })
      );

      setDraftValue((p) => ({ ...p, [optId]: "" }));
    },
    [draftValue, options, setOptions]
  );

  const addSuggestionValue = useCallback(
    (optId, val) => {
      setOptions(
        options.map((o) => {
          if (o.id !== optId) return o;
          return { ...o, values: unique([...(o.values || EMPTY_ARR), val]) };
        })
      );
    },
    [options, setOptions]
  );

  const removeValue = useCallback(
    async (optId, val) => {
      if (hasExistingVariants) {
        const opt = options.find((o) => o.id === optId);
        const optName = opt?.name || "this option";
        const result = await Swal.fire({
          ...businessSwalBase,
          icon: "warning",
          title: "Remove value?",
          html: `Removing <b>${val}</b> from <b>${optName}</b> will affect variant generation.<br><small class="mt-2 block text-neutral-500 dark:text-neutral-400">Variant rows update automatically in the Variants step.</small>`,
          showCancelButton: true,
          confirmButtonText: "Remove",
          cancelButtonText: "Keep",
        });
        if (!result.isConfirmed) return;
      }

      setOptions(
        options.map((o) => {
          if (o.id !== optId) return o;
          return { ...o, values: (o.values || EMPTY_ARR).filter((x) => x !== val) };
        })
      );
    },
    [options, setOptions, hasExistingVariants]
  );

  const updateName = useCallback(
    (optId, name) => {
      // Do NOT trim while typing
      setOptions(options.map((o) => (o.id !== optId ? o : { ...o, name })));
    },
    [options, setOptions]
  );

  const removeOption = useCallback(
    async (optId) => {
      if (hasExistingVariants) {
        const opt = options.find((o) => o.id === optId);
        const optName = opt?.name || "this option";
        const vals = opt?.values || [];
        const result = await Swal.fire({
          ...businessSwalBase,
          icon: "warning",
          title: "Remove option?",
          html: `Removing <b>${optName}</b>${vals.length ? ` (${vals.length} value${vals.length > 1 ? "s" : ""})` : ""} will update generated variants automatically.<br><small class="mt-2 block text-neutral-500 dark:text-neutral-400">Matching rows keep their edited details where possible.</small>`,
          showCancelButton: true,
          confirmButtonText: "Remove",
          cancelButtonText: "Keep",
        });
        if (!result.isConfirmed) return;
      }

      setOptions(options.filter((o) => o.id !== optId));
      setDraftValue((p) => {
        const next = { ...p };
        delete next[optId];
        return next;
      });
    },
    [options, setOptions, hasExistingVariants]
  );

  const addOption = useCallback(() => {
    setOptions([...(options || EMPTY_ARR), { id: makeId(), name: "", values: [] }]);
  }, [options, setOptions]);

  /* ── Template quick-add ── */
  const addTemplate = useCallback(
    (template) => {
      setOptions([
        ...(options || EMPTY_ARR),
        { id: makeId(), name: template.name, values: [...template.defaults] },
      ]);
    },
    [options, setOptions]
  );

  /* ── Option reordering ── */
  const moveOption = useCallback(
    (optId, direction) => {
      const idx = options.findIndex((o) => o.id === optId);
      if (idx < 0) return;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= options.length) return;
      const next = [...options];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      setOptions(next);
    },
    [options, setOptions]
  );

  /* ── Suggestions for known option types ── */
  const getSuggestions = useCallback(
    (optName, currentValues) => {
      const key = clean(optName).toLowerCase();
      const all = SUGGESTIONS[key] || [];
      const existing = new Set(currentValues.map((v) => v.toLowerCase()));
      return all.filter((s) => !existing.has(s.toLowerCase()));
    },
    []
  );

  return (
    <div className="space-y-4">
      {/* Edit impact warning */}
      {hasExistingVariants && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-950/40">
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" /> Editing existing options
          </div>
          <div className="mt-1 text-xs text-amber-800 dark:text-amber-200">
            Changes here may affect generated variants. The variant matrix updates automatically from options.
            Matching variants keep their SKUs and prices where possible.
          </div>
        </div>
      )}

      {/* Status card */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              Product Options
            </div>
            <div className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
              This will generate{" "}
              <span className="font-semibold text-zinc-900 dark:text-zinc-100">
                {variantCount}
              </span>{" "}
              variant{variantCount !== 1 ? "s" : ""}.
              {formulaBreakdown && (
                <span className="ml-1 text-zinc-500 dark:text-zinc-400">
                  ({formulaBreakdown})
                </span>
              )}
            </div>
            {variantCount > VARIANT_DANGER_THRESHOLD && (
              <div className="mt-1 text-xs font-semibold text-red-700 dark:text-red-300">
                Very high variant count — may affect editor performance.
              </div>
            )}
            {variantCount > VARIANT_WARNING_THRESHOLD && variantCount <= VARIANT_DANGER_THRESHOLD && (
              <div className="mt-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                High variant count — consider whether all combinations are necessary.
              </div>
            )}
          </div>

          <span
            className={
              "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold " +
              (ok
                ? "border-emerald-200 bg-white text-emerald-800 dark:border-emerald-900/40 dark:bg-transparent dark:text-emerald-200"
                : "border-amber-200 bg-white text-amber-800 dark:border-amber-900/40 dark:bg-transparent dark:text-amber-200")
            }
          >
            {ok ? "Complete" : "Needs attention"}
          </span>
        </div>

        {!ok && (
          <div className="mt-3 border-t border-zinc-200 pt-3 text-xs text-amber-800 dark:border-zinc-800 dark:text-amber-200">
            <ul className="list-disc space-y-1 pl-5">
              {issues.slice(0, 4).map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Helper tips */}
      <DisclosureLink label="How options create variants" contentClassName="mt-1 text-body-sm text-secondary">
        <ul className="list-disc space-y-1.5 pl-5">
          <li>Each unique combination of options creates one variant (e.g., Size:M + Color:Red).</li>
          <li>You can add multiple values at once using commas: <span className="font-mono">S, M, L, XL</span></li>
          <li>Option order affects SKU generation and display. Use the arrows to reorder.</li>
          <li>Removing values or options updates the generated variants automatically.</li>
        </ul>
      </DisclosureLink>

      {/* Quick-add templates */}
      <div className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]">
        <div className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">Start with a common option</div>
        <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
          Add one with suggested values, then edit it below.
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          {OPTION_TEMPLATES.map((t) => {
            const used = usedNames.has(t.name.toLowerCase());
            return (
              <Button
                key={t.name}
                size="sm"
                icon={used ? Check : Plus}
                onClick={() => addTemplate(t)}
                disabled={used}
                title={used ? `${t.name} already added` : `Add ${t.name}: ${t.defaults.join(", ")}`}
              >
                {t.name}
              </Button>
            );
          })}
        </div>
      </div>

      {/* Option cards */}
      {(options || EMPTY_ARR).map((opt, optIdx) => {
        const id = opt?.id || makeId();
        const name = clean(opt?.name);
        const values = unique(opt?.values);

        const nameBad = !name;
        const valuesBad = name && values.length === 0;
        const isDupName = name && duplicateNames.has(name.toLowerCase());
        const isColor = isColorOption(name);
        const suggestions = name ? getSuggestions(name, values) : [];
        const isFirst = optIdx === 0;
        const isLast = optIdx === options.length - 1;

        return (
          <div
            key={id}
            className="rounded-2xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-[#121214]"
          >
            {/* Header: name + reorder + remove */}
            <div className="flex flex-wrap items-start gap-2 sm:flex-nowrap sm:items-center">
              <div className="flex shrink-0 gap-1">
                <IconButton icon={ArrowUp} variant="secondary" label={`Move ${name || "option"} up`} onClick={() => moveOption(id, -1)} disabled={isFirst} />
                <IconButton icon={ArrowDown} variant="secondary" label={`Move ${name || "option"} down`} onClick={() => moveOption(id, 1)} disabled={isLast} />
              </div>

              <input
                value={opt?.name || ""}
                onChange={(e) => updateName(id, e.target.value)}
                className={
                  "order-first min-h-10 min-w-0 basis-full rounded-xl border bg-white px-3 py-2 text-sm outline-none sm:order-none sm:flex-1 sm:basis-0 " +
                  "focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200 " +
                  "dark:border-zinc-800 dark:bg-[#0f0f10] dark:focus:border-zinc-600 dark:focus:ring-zinc-800 " +
                  (nameBad || isDupName
                    ? "border-red-200 bg-red-50 dark:border-red-900/40 dark:bg-red-950/20"
                    : "border-zinc-200")
                }
                placeholder="Option name (e.g. Size)"
                aria-label={`Option ${optIdx + 1} name`}
              />

              <Button
                variant="quiet"
                icon={Trash2}
                onClick={() => removeOption(id)}
                aria-label={`Remove ${name || `option ${optIdx + 1}`}`}
                className="ml-auto sm:ml-0"
              >
                Remove
              </Button>
            </div>

            {nameBad && (
              <div className="mt-1 text-[11px] text-red-600 dark:text-red-300">
                Option name is required.
              </div>
            )}
            {isDupName && (
              <div className="mt-1 text-[11px] text-red-600 dark:text-red-300">
                Duplicate option name — each option must have a unique name.
              </div>
            )}

            {/* Value input (supports comma-separated) */}
            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              <input
                value={draftValue[id] || ""}
                onChange={(e) => setDraftValue((p) => ({ ...p, [id]: e.target.value }))}
                placeholder={`Add value${name ? ` for ${name}` : ""} (comma-separated, e.g. S, M, L)`}
                className="w-full rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm outline-none
                           focus:border-zinc-400 focus:ring-2 focus:ring-zinc-200
                           dark:border-zinc-800 dark:bg-[#0f0f10] dark:focus:border-zinc-600 dark:focus:ring-zinc-800"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addValues(id);
                  }
                }}
                disabled={nameBad}
                aria-label={`Add values for ${name || `option ${optIdx + 1}`}`}
              />

              <Button
                icon={Plus}
                onClick={() => addValues(id)}
                disabled={nameBad || !clean(draftValue[id])}
                className="w-full sm:w-auto"
              >
                Add values
              </Button>
            </div>

            {/* Suggestion chips */}
            {suggestions.length > 0 && !nameBad && (
              <div className="mt-3">
                <div className="mb-2 text-xs font-medium text-zinc-500 dark:text-zinc-400">Suggestions:</div>
                <div className="flex flex-wrap gap-2">
                  {suggestions.slice(0, 12).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => addSuggestionValue(id, s)}
                      className="inline-flex min-h-7 items-center gap-1 rounded-full border border-dashed border-zinc-300 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600 transition hover:border-zinc-400 hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                      {isColor && getColorHex(s) && (
                        <span
                          className="inline-block h-2.5 w-2.5 rounded-full border border-zinc-300 dark:border-zinc-600"
                          style={{ backgroundColor: getColorHex(s) }}
                        />
                      )}
                      + {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {valuesBad && (
              <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-200">
                Add at least 1 value to generate variants.
              </div>
            )}

            {/* Value chips */}
            <div className="mt-3 flex flex-wrap gap-2">
              {values.map((v) => (
                <span
                  key={v}
                  className="inline-flex min-h-7 items-center gap-1 rounded-full bg-zinc-100 px-2 py-0.5 text-[11px] text-zinc-700
                             dark:bg-zinc-900 dark:text-zinc-200"
                >
                  {isColor && getColorHex(v) && (
                    <span
                      className="inline-block h-2.5 w-2.5 rounded-full border border-zinc-300 dark:border-zinc-600"
                      style={{ backgroundColor: getColorHex(v) }}
                    />
                  )}
                  {v}
                  <button
                    type="button"
                    onClick={() => removeValue(id, v)}
                    className="inline-flex h-5 w-5 items-center justify-center rounded-full text-secondary transition-colors hover:bg-danger-subtle hover:text-danger"
                    title={`Remove ${v}`}
                    aria-label={`Remove ${v} from ${name || "option"}`}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        );
      })}

      <Button icon={Plus} onClick={addOption} className="w-full border-dashed">
        Add another option
      </Button>
    </div>
  );
}
